/**
 * On-Call Assignments API Services
 */

import datacentral from '@/utils/axios/datacentral'
import { ErrorResponse, ResponseArray, ResponseObject } from '@/types/shared'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

import {
    OncallAssignmentResponse,
    GenerateAssignmentsResult,
} from '../types/response'

import {
    AssignmentFilters,
    MyAssignmentsFilters,
    GenerateAssignmentsPayload,
    ReassignPayload,
} from '../types/payload'

export const getOncallAssignments = async (
    payload: DataGridPayload & AssignmentFilters
) => {
    const params = new URLSearchParams({
        page: String(payload?.page || 1),
        page_size: String(payload?.page_size || 10),
        start_date: payload.start_date,
        end_date: payload.end_date,
    })
    if (payload?.q) params.append('q', payload.q)
    if (payload?.filters) params.append('filters', payload.filters)
    if (payload?.schedule_id)
        params.append('schedule_id', String(payload.schedule_id))
    if (payload?.schedule_ids?.length) {
        payload.schedule_ids.forEach((scheduleId) => {
            params.append('schedule_ids', String(scheduleId))
        })
    }
    if (payload?.employee_id)
        params.append('employee_id', String(payload.employee_id))
    if (payload?.status) params.append('status', payload.status)
    if (payload?.source) params.append('source', payload.source)

    const response = await datacentral.get<
        ResponseArray<OncallAssignmentResponse>
    >(`/oncall/assignments?${params.toString()}`)

    // Transform result to results for GokuDataGrid compatibility
    const data = response.data as any
    if (data.result && !data.results) {
        data.results = data.result
    }
    return data
}

export const exportOncallAssignments = async (payload: AssignmentFilters) => {
    const params = new URLSearchParams({
        start_date: payload.start_date,
        end_date: payload.end_date,
    })
    if (payload?.schedule_id)
        params.append('schedule_id', String(payload.schedule_id))
    if (payload?.employee_id)
        params.append('employee_id', String(payload.employee_id))
    if (payload?.status) params.append('status', payload.status)
    if (payload?.source) params.append('source', payload.source)

    const response = await datacentral.get(
        `/oncall/assignments/export?${params.toString()}`,
        { responseType: 'blob' }
    )

    return response.data
}

export const getCurrentOncallAssignments = async () => {
    const response = await datacentral.get<
        ResponseArray<OncallAssignmentResponse>
    >('/oncall/assignments/current')
    return response.data
}

export const getTodayOncallAssignments = async () => {
    const response = await datacentral.get<
        ResponseArray<OncallAssignmentResponse>
    >('/oncall/assignments/today')
    return response.data
}

export const getOncallAssignmentByID = async (
    id: number
): Promise<ResponseObject<OncallAssignmentResponse>> => {
    const response = await datacentral.get<
        ResponseObject<OncallAssignmentResponse>
    >(`/oncall/assignments/${id}`)
    return response.data
}

export const reassignOncallAssignment = async (payload: {
    id: number
    data: ReassignPayload
}): Promise<ResponseObject<OncallAssignmentResponse> | ErrorResponse> => {
    const response = await datacentral.put(
        `/oncall/assignments/${payload.id}/reassign`,
        payload.data
    )
    return response.data
}

export const generateOncallAssignments = async (
    payload: GenerateAssignmentsPayload
): Promise<ResponseObject<GenerateAssignmentsResult> | ErrorResponse> => {
    const response = await datacentral.post(
        '/oncall/assignments/generate',
        payload
    )
    return response.data
}

/** Check oncall assignment conflicts for a given employee and date range */
export const checkOncallConflict = async (params: {
    employee_id: number
    start_date: string
    end_date: string
}): Promise<OncallConflictResult> => {
    const searchParams = new URLSearchParams({
        employee_id: String(params.employee_id),
        start_date: params.start_date,
        end_date: params.end_date,
    })
    const response = await datacentral.get<
        ResponseObject<OncallConflictResult>
    >(`/oncall/assignments/check-conflict?${searchParams.toString()}`)
    return response.data?.result
}

export interface OncallConflictResult {
    has_conflict: boolean
    conflict_count: number
    conflict_dates: string[]
    assignments: OncallAssignmentResponse[]
}

export const getMyAssignments = async (payload: MyAssignmentsFilters) => {
    const params = new URLSearchParams({
        start_date: payload.start_date,
        end_date: payload.end_date,
    })

    const response = await datacentral.get<
        ResponseArray<OncallAssignmentResponse>
    >(`/oncall/assignments/my?${params.toString()}`)

    // Transform result to results for compatibility
    const data = response.data as any
    if (data.result && !data.results) {
        data.results = data.result
    }
    return data
}
